/**
 * View component for /content-modules/:contentModuleId
 *
 * Displays a single contentModule from the 'byId' map in the contentModule reducer
 * as defined by the 'selected' property
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, history, withRouter } from 'react-router-dom';


// import third-party libraries
// let Draggable = require('react-draggable'); //https://github.com/mzabriskie/react-draggable/pull/217/files
let Draggable = require('../../../global/lib/react-draggable/');
import classNames from 'classnames';

// import global components
import Base from "../../../global/components/BaseComponent.js.jsx";

// import utilities
import graphUtils from "../../../global/utils/graphUtils.js";
import videoUtils from "../../../global/utils/videoUtils.js";

// import graph components
import GraphNode from '../../node/components/GraphNode.js.jsx'
import GraphTransition from '../../transition/components/GraphTransition.js.jsx'
import NewGraphTransition from '../../transition/components/NewGraphTransition.js.jsx'
import MetricSelectorSearchItem from '../../metric/components/MetricSelectorSearchItem.js.jsx';

class ContentModuleGraph extends Base {
  constructor(props) {
    super(props);

    this.state = {
      draggingNode: {
        id: null
        , x: null
        , y: null
      }
      , graphMouseX: null
      , graphMouseY: null
      , newTransition: null // are we dragging  new transition?
    }

    this._bind(
      // node handlers
      '_handleNodeDrag' // draw new lines as node moves
      ,'_handleNodeDragEnd' // pass to parent to save

      // transition handlers
      , '_onStartTransition' // draw dummy line on graph

      // graph handlers
      , '_handleGraphDoubleClick' // tell parent to create new node at location
      , '_handleGraphMouseMove' // track mouse coordinates
      , '_handleGraphMouseUp' // catch to cancel creating a transition
      , '_handleGraphPanEnd' // tell parent where we finished panning to
      , '_handleNodeMouseUp' // catch on our child nodes to create transitions
    )
  }

  componentDidMount() {
    // attach listeners for mouse position
    document.addEventListener('mousemove', this._handleGraphMouseMove);
    // data loading happens in parent
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this._handleGraphMouseMove);
  }

  // 1. node handlers
  _handleNodeDrag(node, x, y) {
    // we need to catch for node movement to update the transition svg's
    // transitions don't inherently have anything to display; the line is entirely based on it's nodes' positions
    // the build page handles saving once the drag has finished
    // console.log('_handleNodeDrag')
    this.setState({
      draggingNode: {
        id: node._id
        , x
        , y
      }
    })
  }

  _handleNodeDragEnd(node, x, y) {
    // forward action to build component
    this.setState({
      draggingNode: {
        id: null
        , x: null
        , y: null
      }
    })
    this.props.handleNodeDragEnd(node, x, y);
  }

  // 2. transition handlers
  _onStartTransition(node) {
    // console.log("Starting transition on node", node)
    this.setState({
      newTransition: {
        _startNode: node._id
      }
    })
  }

  _handleGraphMouseMove(e) {
    //find position of mouse ON THE GRAPH and save it to the state - see _handleGraphDoubleClick for math notes
    const boundingRect = this.refs.graph_foreground.getBoundingClientRect();
    const scaledX = (e.clientX - boundingRect.left) / (this.props.graphParams.scale/10)
    const scaledY = (e.clientY - boundingRect.top)/(this.props.graphParams.scale/10)
    this.setState({
      graphMouseX: scaledX
      , graphMouseY: scaledY
    })
  }

  _handleGraphMouseUp(e) {
    // console.log("GRAPH mouse up")
    if(this.state.newTransition) {
      this.setState({
        newTransition: null
      })
    }
  }

  _handleNodeMouseUp(node) {
    // console.log("GRAPH node up", node)
    // if we have a mouse up inside of a node AND state.newTransition is there, that means we need to create one
    if(this.state.newTransition) {
      const newTransition = {
        ...this.state.newTransition
        , _endNode: node._id
      }
      // send this proto-transition to the parent to validate and save
      this.props.handleNewTransition(newTransition);
      // go ahead and reset the state while we wait for the transition to save
      this.setState({
        newTransition: null
      })
    } // else it doesn't mean anything
  }

  // 3. graph handlers
  _handleGraphPanEnd(e, ui) {
    this.props.handleGraphPanEnd(ui.x, ui.y)
    // because this is only called when clicking on a non-node on the graph, we can use to to cancel selected as well
    if(this.props.selectedNode) {
      this.props.handleNodeDeselect();
    }
  }

  _handleGraphDoubleClick(e) {
    // use to create a new node

    // find position in reference to the graph
    // we can get position relative to the page via event.clientX, event.clientY
    // find page offset of the SVG (which should be the same size as the graph) https://developer.mozilla.org/en-US/docs/Web/API/Element/getBoundingClientRect
    const boundingRect = this.refs.graph_foreground.getBoundingClientRect();
    // console.log(e.clientX - boundingRect.left, e.clientY - boundingRect.top)
    // console.log(this.props.graphParams.scale/10 * (e.clientX - boundingRect.left), this.props.graphParams.scale/10 * (e.clientY - boundingRect.top))
    // console.log((e.clientX - boundingRect.left) / (this.props.graphParams.scale/10), (e.clientY - boundingRect.top)/(this.props.graphParams.scale/10))
    const scaledX = (e.clientX - boundingRect.left) / (this.props.graphParams.scale * 0.1)
    const scaledY = (e.clientY - boundingRect.top) / (this.props.graphParams.scale * 0.1)
    // return offset-ed position to the parent
    this.props.handleGraphDoubleClick(scaledX, scaledY);
  }

  render() {
    const {
      graphParams
      , handleGraphScale
      , metricMap
      , nodes
      , nodeStore
      , resetPanAndScale
      , sceneStore
      , transitions
    } = this.props;

    // console.log("RENDERING GRAPH with");
    return (
      <div className="tm-node-builder" onMouseUp={this._handleGraphMouseUp}>
        <div className="zoom-level-wrapper">
          <div className="-buttons">
            <button className="yt-btn small link" type="button" onClick={() => handleGraphScale(graphParams.scale - 1)}> <i className="fal fa-minus"/> </button>
            <span className="-indicator">{graphParams.scale * 10}%</span>
            <button className="yt-btn small link" type="button" onClick={() => handleGraphScale(graphParams.scale + 1)}> <i className="fal fa-plus"/> </button>
          </div>
          <div className="-buttons">
            <button className="yt-btn small link" type="button" onClick={() => resetPanAndScale()}> <i className="fal fa-sync"/> </button>
          </div>
        </div>
        <svg
          id="graph_background"
          className="graph_background"
          ref="graph_background"
          // try to offset the grid so that it's always centered on the graph and not set to the top left
          viewBox={`${(graphParams.xOffset * .25)},${(graphParams.yOffset * .25)},${5000/(graphParams.scale * 0.1)},${5000/(graphParams.scale * 0.1)}`}
        >
          <g>
            <defs>
              <pattern id="grid" width="12" height="12" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="0.5" fill="#BCC7D1"></circle>
              </pattern>
              <pattern id="grid2" width="24" height="24" patternUnits="userSpaceOnUse">
                <rect x="0" y="0" width="24" height="24" fill="url(#grid)"></rect>
                <circle cx="1" cy="1" r="1" fill="#C8D3DE"></circle>
              </pattern>
            </defs>
            <rect x={`${graphParams.xOffset}`} y={`${graphParams.yOffset}`} width={`${graphParams.width}`} height={`${graphParams.height}`} fill="url(#grid2)" transform="translate(-1,-1)"></rect>
          </g>
        </svg>
        <Draggable
          defaultPosition={{x: graphParams.xOffset, y: graphParams.yOffset}}
          onStop={this._handleGraphPanEnd}
          disabled={false}
          // axis="x" //TODO: prevent dragging off of the screen
          // bounds={{
          //   left:
          //   , right: 1000
          // }}
        >

          <div className="graph_wrapper">
            <div
              id="graph_id"
              className="graph_class"
              style={
                { height: graphParams.height
                  , transform: `scale(${graphParams.scale*0.1})`
                  , width: graphParams.width
                }}
            >
              <svg
                id="graph_foreground"
                className="graph_foreground"
                ref="graph_foreground"
                onDoubleClick={this._handleGraphDoubleClick}
              >
                <defs>
                  <marker
                    id="arrow-selected"
                    markerWidth="100" markerHeight="100" refX="0" refY="3" orient="auto"
                    markerUnits="strokeWidth"
                  >
                    <path d="M0,0 L0,6 L9,3 z" fill="black" />
                  </marker>
                  <marker
                    id="arrow-default"
                    markerWidth="100" markerHeight="100" refX="0" refY="3" orient="auto"
                    markerUnits="strokeWidth"
                  >
                    <path d="M0,0 L0,6 L9,3 z" fill="darkgray" />
                  </marker>
                  <marker
                    id="arrow-fromSelected"
                    markerWidth="100" markerHeight="100" refX="0" refY="3" orient="auto"
                    markerUnits="strokeWidth"
                  >
                    <path d="M0,0 L0,6 L9,3 z" fill="teal" />
                  </marker>
                  <marker
                    id="arrow-toSelected"
                    markerWidth="100" markerHeight="100" refX="0" refY="3" orient="auto"
                    markerUnits="strokeWidth"
                  >
                    <path d="M0,0 L0,6 L9,3 z" fill="purple" />
                  </marker>
                  <marker
                    id="arrow-new"
                    markerWidth="100" markerHeight="100" refX="0" refY="3" orient="auto"
                    markerUnits="strokeWidth"
                  >
                    <path d="M0,0 L0,6 L9,3 z" fill="magenta" />
                  </marker>
                </defs>

                { transitions ?
                  transitions.map((transition, i) =>
                    <GraphTransition
                      draggingNode={this.state.draggingNode}
                      endNode={nodeStore.byId[transition._endNode]}
                      endNodeSelected={this.props.selectedNode && this.props.selectedNode._id == transition._endNode}
                      key={transition._id + i}
                      onRemove={this.props.handleRemoveTransition}
                      startNode={nodeStore.byId[transition._startNode]}
                      transition={transition}
                      graphMouseX={this.state.graphMouseX}
                      graphMouseY={this.state.graphMouseY}
                      startNodeSelected={this.props.selectedNode && this.props.selectedNode._id == transition._startNode}
                    />
                  )
                  :
                  null
                }
                { this.state.newTransition ?
                  // check if we are trying to create a new transition. if so, start drawing it on the graph
                  <NewGraphTransition
                    key={"GJAY5Y7THHATHA68282826HYSJSYRMUKKU"}
                    startNode={nodeStore.byId[this.state.newTransition._startNode]}
                    graphMouseX={this.state.graphMouseX}
                    graphMouseY={this.state.graphMouseY}
                    transition={this.state.newTransition}
                  />
                : null
                }
              </svg>
              { nodes ?
                nodes.map((node, i)=>
                  <GraphNode
                    key={node._id + i}
                    handleOpenModal={this.props.handleOpenModal}
                    metrics={node._metrics && node._metrics.map(mId => metricMap[mId])}
                    node={node}
                    onDrag={this._handleNodeDrag}
                    onDragEnd={this._handleNodeDragEnd}
                    onDragStart={this.props.handleNodeDragStart}
                    onDeselect={this.props.handleNodeDeselect}
                    onSelect={this.props.handleNodeSelect}
                    onStartTransition={this._onStartTransition}
                    onMouseUp={this._handleNodeMouseUp}
                    scale={this.props.graphParams.scale*0.1} //actual scaling is percentage based, so "9" is really 90%
                    scene={sceneStore.byId[node._scene]}
                    selected={this.props.selectedNode && this.props.selectedNode._id == node._id}
                    selectedScene={this.props.selectedScene}
                    selectedContentModuleId={this.props.selectedContentModuleId}
                    unlinkScene={this.props.handleUnlinkScene}
                  />
                )
                : null
              }


            </div>
          </div>
        </Draggable>
      </div>
    )
  }
}

ContentModuleGraph.propTypes = {
  dispatch: PropTypes.func.isRequired
  , graphParams: PropTypes.object
  , handleNewTransition: PropTypes.func
  , handleNodeDeselect: PropTypes.func
  , handleNodeDragEnd: PropTypes.func
  , handleNodeDragStart: PropTypes.func
  , handleNodeSelect: PropTypes.func
  , handleGraphDoubleClick: PropTypes.func
  , handleGraphPanEnd: PropTypes.func
  , handleGraphScale: PropTypes.func.isRequired
  , handleOpenModal: PropTypes.func.isRequired
  , handleRemoveTransition: PropTypes.func
  , handleUnlinkScene: PropTypes.func
  , nodes: PropTypes.array
  , resetPanAndScale: PropTypes.func.isRequired
  , selectedNode: PropTypes.object
  , selectedScene: PropTypes.object
  , selectedContentModuleId: PropTypes.string
  , transitions: PropTypes.array
}

ContentModuleGraph.defaultProps = {

}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  return {
    metricMap: store.metric.byId 
    , nodeStore: store.node
    , sceneStore: store.scene
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(ContentModuleGraph)
);



// WEBPACK FOOTER //
// modules/contentModule/components/ContentModuleGraph.js.jsx